import React, { useState, useEffect, useRef } from "react";
import { Controller } from "react-hook-form";
import ColorPickerPopover from "@/app/shared/colorpicker-popover"; // Update the import path
import { IColorScheme } from "@/utilities/types/ColorScheme.ts";
import { IColors } from "@/utilities/types/Color.ts";
import ColorSchemaPreview from "@/components/ui/color-scheme-preview.tsx";
import transformColorVariable from "@/utilities/functions/transform-color-variable.ts";
import { LoaderCircle } from "lucide-react";

interface ColorSchemaFieldProps {
  title: string;
  name: string;
  control: any;
  errors: any;
  colors: IColors;
  colorScheme: IColorScheme;
  previewUrl: string;
}

const isValidColor = (color: string) => {
  const s = new Option().style;
  s.color = color;
  return s.color !== "";
};

const ColorSchemaFieldComponent: React.FC<ColorSchemaFieldProps> = ({
  title,
  name,
  control,
  errors,
  colors,
  colorScheme,
  previewUrl,
}) => {
  const [schemeColors, setSchemeColors] = useState<IColors>(colors);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isInView, setIsInView] = useState(false);

  // Sync schemeColors when colors prop changes (e.g., when form resets)
  useEffect(() => {
    setSchemeColors(colors);
  }, [colors]);

  // Lazy mount: only render the iframe when the component is near the viewport.
  // Once visible, keep it mounted (disconnect) so color picker changes don't
  // re-trigger the observer.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: "200px" },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const handleColorChange = (key: string, value: string) => {
    setSchemeColors((prevColors) => ({
      ...prevColors,
      [key]: value,
    }));
  };

  const previewOrigin =
    import.meta.env.DEV && import.meta.env.VITE_BASE_WEB_URL
      ? String(import.meta.env.VITE_BASE_WEB_URL).replace(/\/$/, "")
      : `https://${previewUrl}`;

  return (
    <div
      ref={containerRef}
      className="flex md:flex-row flex-col rounded-lg bg-muted p-6 gap-6"
    >
      <div className="grow p-0 md:p-6 flex items-center justify-center rounded-lg bg-white min-h-[420px]">
        {isInView ? (
          <ColorSchemaPreview
            colors={schemeColors}
            baseUrl={`${previewOrigin}${colorScheme?.slug}`}
          />
        ) : (
          <div className="flex flex-col items-center gap-2 text-muted-foreground">
            <LoaderCircle className="w-8 h-8 text-primary animate-spin" />
            <span className="text-xs">Loading preview…</span>
          </div>
        )}
      </div>
      <div className="shrink-0 flex flex-col gap-3">
        <p className="font-bold text-2xl">{title}</p>
        {Object.keys(colors).map((colorKey) => {
          const colorValue = colors[colorKey];
          return (
            <div key={colorKey} className="">
              <p className="rizzui-input-label block text-sm mb-1.5 font-medium text-muted-foreground">
                {transformColorVariable(colorKey, title)}
              </p>

              <Controller
                key={colorKey}
                control={control}
                name={`${name}.${colorKey}`}
                defaultValue={colorValue}
                rules={{
                  validate: {
                    isValidColor: (value) =>
                      isValidColor(value) || "Invalid color value",
                  },
                }}
                render={({ field }) => (
                  <div className="relative h-full flex flex-col">
                    <ColorPickerPopover
                      value={field.value || ""}
                      onChange={(value: string) => {
                        handleColorChange(colorKey, value);
                        field.onChange(value);
                      }}
                      className="w-full h-[42px] flex-1"
                    />
                    {errors[name] && errors[name][colorKey] && (
                      <p className="text-red text-[13px] mt-1.5 rizzui-input-error-text">
                        {errors[name][colorKey].message}
                      </p>
                    )}
                  </div>
                )}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Memo to prevent all 25 fields from re-rendering when one color picker
// changes, or when unrelated form fields update.
export const ColorSchemaField = React.memo(ColorSchemaFieldComponent);
